import React, { useState, useRef, useEffect } from 'react';
import { FileDown, ChevronDown, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { downloadSectionsCSV, downloadSectionsExcel, downloadSectionsPDF } from '../utils/exportHelpers';

// One export button for every report: CSV, Excel and PDF of what is on the
// screen, and — when the report has a "detailed" version — Excel and PDF of
// the underlying rows (every invoice, payment, fuel slip…) as well.
//
// `summary` and `detailed` are report descriptions:
//   { filename, title, subtitle, sections: [{ name, columns, rows, totals }] }
// where columns are { header, value(row), align, width }. `detailed` may be
// a function returning a Promise of one (fetched only when asked for).
export default function ExportMenu({ summary, detailed, disabled, label = 'Export' }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const box = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const run = async (kind, fmt) => {
    setOpen(false);
    setBusy(kind + fmt);
    try {
      const report = kind === 'detailed'
        ? (typeof detailed === 'function' ? await detailed() : detailed)
        : summary;
      if (!report || !report.sections?.length) { toast.error('Nothing to export'); return; }
      const base = report.filename.replace(/\.(csv|xlsx|pdf)$/i, '') + (kind === 'detailed' ? '-detailed' : '');
      if (fmt === 'csv') downloadSectionsCSV(report, `${base}.csv`);
      if (fmt === 'excel') downloadSectionsExcel(report, `${base}.xlsx`);
      if (fmt === 'pdf') downloadSectionsPDF(report, `${base}.pdf`);
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || 'Export failed');
    } finally {
      setBusy('');
    }
  };

  const Item = ({ kind, fmt, icon: Icon, children }) => (
    <button type="button" onClick={() => run(kind, fmt)}
      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-orange-50">
      <Icon size={14} className="text-gray-400" /> {children}
    </button>
  );

  return (
    <div ref={box} className="relative inline-block">
      <button type="button" disabled={disabled || !!busy} onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} {label} <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-xl">
          <Item kind="summary" fmt="csv" icon={FileText}>CSV</Item>
          <Item kind="summary" fmt="excel" icon={FileSpreadsheet}>Excel</Item>
          <Item kind="summary" fmt="pdf" icon={FileText}>PDF</Item>
          {detailed && (
            <>
              <div className="my-1 border-t border-gray-100" />
              <p className="px-3 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Detailed, every line</p>
              <Item kind="detailed" fmt="excel" icon={FileSpreadsheet}>Detailed Excel</Item>
              <Item kind="detailed" fmt="pdf" icon={FileText}>Detailed PDF</Item>
            </>
          )}
        </div>
      )}
    </div>
  );
}
